'use strict';

const csrf = require('public/js/lib/csrf/utils');
const { injectTokenIntoForm } = require('public/js/lib/csrf/form-injector');

/**
 * @param {HTMLFormElement} form
 * @returns {boolean}
 */
function formHasToken(form) {
	const csrfConfig = csrf.getCsrfConfig();
	const input = form.querySelector(`input[name="${csrfConfig.field_name}"]`);
	return !!(input && String(input.getAttribute('value') || '').trim() !== '');
}

/**
 * @param {HTMLFormElement} form
 * @param {function(): void} replay
 * @param {function(Promise): Promise} raceWithTimeout
 * @param {function(): Promise<string>} [refreshCsrfToken]
 * @param {function(): void} [notifyCsrfTimeout]
 * @returns {void}
 */
function injectTokenAndReplay(form, replay, raceWithTimeout, refreshCsrfToken, notifyCsrfTimeout) {
	injectTokenIntoForm(form);
	if (formHasToken(form)) {
		replay();
		return;
	}

	if (typeof refreshCsrfToken !== 'function') {
		replay();
		return;
	}

	const recover = () => {
		if (typeof notifyCsrfTimeout === 'function') {
			notifyCsrfTimeout();
		}
	};

	const onRefreshed = (token) => {
		if (token) {
			injectTokenIntoForm(form);
		}

		if (formHasToken(form)) {
			replay();
			return;
		}

		recover();
	};

	Promise.resolve()
		.then(() => raceWithTimeout(refreshCsrfToken()))
		.then(onRefreshed)
		.catch(recover);
}

/**
 * Returns true when submit should wait for csrf bootstrap.
 * @param {unknown} candidate
 * @returns {boolean}
 */
function shouldGateFormSubmit(candidate) {
	const form = candidate instanceof HTMLFormElement ? candidate : null;
	if (!form) {
		return false;
	}

	if (form.__rp_csrf_replaying_submit === true) {
		return false;
	}

	const method = String(form.getAttribute('method') || 'GET');
	const action = String(form.getAttribute('action') || '');
	if (!csrf.isMutatingMethod(method) || !csrf.isSameOriginFormAction(action)) {
		return false;
	}

	const csrfConfig = csrf.getCsrfConfig();
	const csrfInputExists = form.querySelector(`input[name="${csrfConfig.field_name}"]`);
	if (csrfInputExists && String(csrfInputExists.getAttribute('value') || '').trim() !== '') {
		return false;
	}

	const ready = globalThis.__rp_csrf_ready_promise;
	return !!(ready && typeof ready.then === 'function');
}

/**
 * Installs a form submit interceptor that pauses early mutating submits
 * until csrf bootstrap has had a chance to inject token inputs.
 * @param {function(Promise): Promise} raceWithTimeout
 * @param {function(): Promise<string>} [refreshCsrfToken]
 * @param {function(): void} [notifyCsrfTimeout]
 * @returns {void}
 */
function installFormSubmitInterceptor(raceWithTimeout, refreshCsrfToken, notifyCsrfTimeout) {
	try {
		if (globalThis.__rp_csrf_submit_interceptor_installed) {
			return;
		}

		document.addEventListener('submit', (event) => {
			try {
				const form = event && event.target instanceof HTMLFormElement ? event.target : null;
				if (!shouldGateFormSubmit(form)) {
					return;
				}

				event.preventDefault();

				const replay = () => {
					form.__rp_csrf_replaying_submit = true;
					try {
						if (typeof form.requestSubmit === 'function') {
							form.requestSubmit();
						} else if (typeof form.submit === 'function') {
							form.submit();
						}
					} finally {
						form.__rp_csrf_replaying_submit = false;
					}
				};

				const resumeSubmit = () => injectTokenAndReplay(form, replay, raceWithTimeout, refreshCsrfToken, notifyCsrfTimeout);

				const ready = globalThis.__rp_csrf_ready_promise;
				raceWithTimeout(ready).then(resumeSubmit, resumeSubmit);
			} catch (e) {
				console.warn('[csrf-bootstrap] submit intercept failed for form', e); // eslint-disable-line no-console
			}
		}, true);

		globalThis.__rp_csrf_submit_interceptor_installed = true;
	} catch (e) {
		console.warn('[csrf-bootstrap] installFormSubmitInterceptor failed', e); // eslint-disable-line no-console
	}
}

/**
 * Wraps jQuery's $.fn.submit so that no-arg calls (programmatic triggers)
 * are deferred until CSRF bootstrap resolves. This catches cases like
 * rp$('#form').submit() which bypass native DOM submit events.
 * @param {unknown} jq - Candidate jQuery instance.
 * @param {function(Promise): Promise} raceWithTimeout
 * @param {function(): Promise<string>} [refreshCsrfToken]
 * @param {function(): void} [notifyCsrfTimeout]
 * @returns {void}
 */
function installJQuerySubmitHook(jq, raceWithTimeout, refreshCsrfToken, notifyCsrfTimeout) {
	try {
		if (typeof jq !== 'function' || !jq.fn || jq.fn.submit.__rp_csrf_wrapped) {
			return;
		}

		const originalSubmit = jq.fn.submit;

		jq.fn.submit = function rpCsrfSubmitWrapper() {
			if (arguments.length > 0) {
				return originalSubmit.apply(this, arguments);
			}

			const form = this[0];
			if (!(form instanceof HTMLFormElement) || !shouldGateFormSubmit(form)) {
				return originalSubmit.apply(this, arguments);
			}

			const ready = globalThis.__rp_csrf_ready_promise;

			const replay = () => {
				form.__rp_csrf_replaying_submit = true;
				try {
					originalSubmit.call(this);
				} finally {
					form.__rp_csrf_replaying_submit = false;
				}
			};

			const resumeSubmit = () => injectTokenAndReplay(form, replay, raceWithTimeout, refreshCsrfToken, notifyCsrfTimeout);

			raceWithTimeout(ready).then(resumeSubmit, resumeSubmit);
			return this;
		};

		jq.fn.submit.__rp_csrf_wrapped = true;
	} catch (e) {
		console.warn('[csrf-bootstrap] installJQuerySubmitHook failed', e); // eslint-disable-line no-console
	}
}

/**
 * Installs jQuery submit hooks for known jQuery instances.
 * @param {function(Promise): Promise} raceWithTimeout
 * @param {function(): Promise<string>} [refreshCsrfToken]
 * @param {function(): void} [notifyCsrfTimeout]
 * @returns {void}
 */
function installJQuerySubmitHooks(raceWithTimeout, refreshCsrfToken, notifyCsrfTimeout) {
	try {
		const candidates = [globalThis.jQuery, globalThis.rp$];
		const seen = new Set();

		for (const candidate of candidates) {
			if (!candidate || seen.has(candidate)) {
				continue;
			}
			seen.add(candidate);
			installJQuerySubmitHook(candidate, raceWithTimeout, refreshCsrfToken, notifyCsrfTimeout);
		}
	} catch (e) {
		console.warn('[csrf-bootstrap] installJQuerySubmitHooks failed', e); // eslint-disable-line no-console
	}
}

module.exports = {
	installFormSubmitInterceptor,
	installJQuerySubmitHooks,
};
